import { addBreadcrumb, captureEvent, captureException } from "@sentry/react";
import type { FetchResponseOfError } from "Infrastructure/Api/Api";
import { getApiErrorResponse } from "Infrastructure/Api/ApiUtils";
import { isConnectSupersededError } from "Utils/ConnectSupersededError";
import { isGuid } from "Utils/StringUtils";
import { MS_TIME } from "Utils/TimeUtils";

/**
 * Error reported to Sentry for a failed API request. The message is built
 * from the normalized endpoint, and grouping is forced by the
 * [endpoint, status] fingerprint, so one broken endpoint is one issue - not
 * one issue per affected query.
 */
class ApiRequestError extends Error {
  constructor(status: number, endpoint: string) {
    super(`API request failed with status ${status}: ${endpoint}`);
    this.name = "ApiRequestError";
  }
}

const NUMERIC_SEGMENT_PATTERN = /^\d+$/;

/**
 * Normalizes "https://host/api/parties/3fa85f64-.../relations?x=1" to
 * "/api/parties/{id}/relations" so fingerprints stay stable across entities.
 */
const getEndpoint = (response: FetchResponseOfError): string => {
  const url = response.args?.url;

  if (typeof url !== "string" || url.length === 0) {
    return "unknown-endpoint";
  }

  const withoutOrigin = url.replace(/^[a-z]+:\/\/[^/]+/i, "");
  const queryIndex = withoutOrigin.indexOf("?");
  const path =
    queryIndex === -1 ? withoutOrigin : withoutOrigin.slice(0, queryIndex);

  return path
    .split("/")
    .map((segment) =>
      isGuid(segment) || NUMERIC_SEGMENT_PATTERN.test(segment)
        ? "{id}"
        : segment,
    )
    .join("/");
};

const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === "AbortError";

/**
 * One Sentry event per grouping key per window. An always-mounted query (the chat token behind
 * the unread badge, the Stream connect itself, the stories token) refetches an errored entry on
 * every window focus and remount, so one broken endpoint or one Stream outage would otherwise
 * cost one event per focus per user for its whole length — all grouped into the same issue, all
 * burning quota. Within the window the repeat is kept as a breadcrumb; the next window reports
 * again, so a persisting outage still shows continued activity on the issue.
 */
const REPORT_WINDOW_MS = 10 * MS_TIME.minute;
const lastReportedAt = new Map<string, number>();

/**
 * True (and a breadcrumb is left instead) when the same key was reported within the window.
 * The key is whatever Sentry groups the event by: the [endpoint, status] fingerprint for API
 * responses, name + message for other throws.
 */
const isRepeatWithinWindow = (
  key: readonly string[],
  message: string,
  data?: Record<string, unknown>,
): boolean => {
  const mapKey = JSON.stringify(key);
  const now = Date.now();
  const last = lastReportedAt.get(mapKey);

  if (last !== undefined && now - last < REPORT_WINDOW_MS) {
    addBreadcrumb({
      category: "api.error",
      level: "error",
      message: `${message} (repeat, not reported)`,
      data,
    });
    return true;
  }

  lastReportedAt.set(mapKey, now);
  return false;
};

/**
 * Reports a failed API call to Sentry. Wired into the QueryCache and
 * MutationCache in queryClient.ts - never shows any UI. Components that want
 * user-facing feedback opt in at the hook or call site with their own
 * translated message.
 */
export const reportApiError = (error: unknown): void => {
  // A cancelled request (navigation, component unmount) or a Stream connect that a newer one
  // superseded (persona switch, logout) is not an error.
  if (isAbortError(error) || isConnectSupersededError(error)) {
    return;
  }

  const response = getApiErrorResponse(error);

  // A thrown JS Error without a response shape - a bug in a select/mapper or
  // a hand-rolled mutationFn throw. Default grouping by stack trace.
  if (response === undefined) {
    const thrown =
      error instanceof Error
        ? error
        : new Error(`Non-error thrown from an API call: ${String(error)}`);

    // Stream SDK failures (connect, unread badge) land here and repeat per focus during an
    // outage; their messages are stable per failure kind, so name + message is the group.
    if (isRepeatWithinWindow([thrown.name, thrown.message], thrown.message)) {
      return;
    }

    captureException(thrown);
    return;
  }

  // ErrorStatuses claims 0 | 5xx, but the generated client casts every real
  // status into it (Api.ts errorResponse) - widen to number for comparisons.
  const status: number = response.status;
  const endpoint = getEndpoint(response);

  if (status === 403) {
    if (
      isRepeatWithinWindow(
        [endpoint, String(status)],
        `Forbidden: ${endpoint}`,
        {
          status,
          url: response.args?.url,
        },
      )
    ) {
      return;
    }

    captureEvent({
      message: `Forbidden: ${endpoint}`,
      level: "warning",
      extra: { status, url: response.args?.url },
    });
    return;
  }

  // Only server-side failures become Sentry events. 401 logs the user out via
  // the auth response middleware in App.tsx, status 0 (network failure) is
  // owned by the OfflineOverlay, and the remaining 4xx are outcomes the UI
  // owns - validation, not-found, conflict - that would mostly burn quota on
  // user errors. Keep them as breadcrumbs in the trail of a real error.
  if (status < 500) {
    addBreadcrumb({
      category: "api.error",
      level: "warning",
      message: `API request failed with status ${status}: ${endpoint}`,
      data: { status, url: response.args?.url },
    });
    return;
  }

  const fingerprint = [endpoint, String(status)];
  const requestError = new ApiRequestError(status, endpoint);

  if (
    isRepeatWithinWindow(fingerprint, requestError.message, {
      status,
      url: response.args?.url,
    })
  ) {
    return;
  }

  captureException(requestError, {
    fingerprint,
    extra: {
      status,
      url: response.args?.url,
      problemDetails: response.data ?? response.error,
    },
  });
};
